import React, { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';

interface AnalyticsDashboardProps {
  session: Session;
}

interface DashboardOverview {
  total_cases: number;
  active_cases: number;
  closed_cases: number;
  total_documents: number;
  pending_deadlines: number;
  overdue_deadlines: number;
  high_risk_cases: number;
  unread_messages: number;
  recent_activity_count: number;
}

interface ProductivityScore {
  score: number;
  grade: string;
  factors: {
    cases_created: number;
    cases_closed: number;
    documents_uploaded: number;
    activity_days: number;
    overdue_cases: number;
  };
}

interface Insight {
  id: string;
  insight_type: string;
  title: string;
  description: string;
  severity: string;
  data: any;
  is_read: boolean;
  created_at: string;
}

interface TrendData {
  date: string;
  cases_created: number;
  cases_closed: number;
  cumulative_active: number;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ session }) => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [productivityScore, setProductivityScore] = useState<ProductivityScore | null>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setIsLoading(true);

    // Fetch overview
    const { data: overviewData } = await supabase.rpc('get_dashboard_overview', {
      p_user_id: session.user.id
    });

    // Fetch productivity score
    const { data: scoreData } = await supabase.rpc('get_productivity_score', {
      p_user_id: session.user.id,
      p_days: 7
    });

    // Generate insights
    await supabase.rpc('generate_productivity_insights', {
      p_user_id: session.user.id
    });

    // Fetch insights
    const { data: insightsData } = await supabase
      .from('productivity_insights')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('is_dismissed', false)
      .order('created_at', { ascending: false })
      .limit(5);

    // Fetch trends
    const { data: trendsData } = await supabase.rpc('get_case_trends', {
      p_user_id: session.user.id,
      p_days: 30
    });

    if (overviewData && overviewData.length > 0) {
      setOverview(overviewData[0]);
    }

    if (scoreData && scoreData.length > 0) {
      setProductivityScore(scoreData[0]);
    }

    if (insightsData) {
      setInsights(insightsData);
    }

    if (trendsData) {
      setTrends(trendsData);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
  }, [session.user.id]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'from-green-500 to-emerald-500';
    if (score >= 60) return 'from-blue-500 to-cyan-500';
    if (score >= 40) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-500';
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'success': return 'check_circle';
      default: return 'info';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-500';
      case 'warning': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-500';
      case 'success': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-500';
      default: return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-500';
    }
  };

  const dismissInsight = async (insightId: string) => {
    await supabase
      .from('productivity_insights')
      .update({ is_dismissed: true })
      .eq('id', insightId);
    
    setInsights(insights.filter(i => i.id !== insightId));
  };

  if (isLoading || !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-symbols-outlined animate-spin text-4xl text-luna-secondary">sync</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Productivity Score Card */}
      {productivityScore && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Produktivitäts-Score
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Letzte 7 Tage
              </p>
            </div>
            <div className={`text-4xl font-bold bg-gradient-to-r ${getScoreColor(productivityScore.score)} text-transparent bg-clip-text`}>
              {productivityScore.grade}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {productivityScore.score}/100
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {productivityScore.score >= 80 ? 'Exzellent!' : 
                 productivityScore.score >= 60 ? 'Gut!' :
                 productivityScore.score >= 40 ? 'Fair' : 'Verbesserung möglich'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full bg-gradient-to-r ${getScoreColor(productivityScore.score)} transition-all`}
                style={{ width: `${productivityScore.score}%` }}
              />
            </div>
          </div>

          {/* Score Factors */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {productivityScore.factors.cases_created}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Fälle erstellt</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {productivityScore.factors.cases_closed}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Fälle geschlossen</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {productivityScore.factors.documents_uploaded}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Dokumente</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {productivityScore.factors.activity_days}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Aktive Tage</div>
            </div>
            <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-xl font-bold text-red-600 dark:text-red-400">
                {productivityScore.factors.overdue_cases}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Überfällig</div>
            </div>
          </div>
        </div>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-3xl text-blue-600 dark:text-blue-400">
              folder
            </span>
            <div>
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {overview.total_cases}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">Gesamt-Fälle</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-3xl text-green-600 dark:text-green-400">
              pending_actions
            </span>
            <div>
              <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                {overview.active_cases}
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">Aktive Fälle</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-3xl text-purple-600 dark:text-purple-400">
              description
            </span>
            <div>
              <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                {overview.total_documents}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-300">Dokumente</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-3xl text-orange-600 dark:text-orange-400">
              schedule
            </span>
            <div>
              <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                {overview.pending_deadlines}
              </div>
              <div className="text-xs text-orange-700 dark:text-orange-300">Bevorstehend</div>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Erkenntnisse & Empfehlungen
          </h3>

          <div className="space-y-3">
            {insights.map((insight) => (
              <div
                key={insight.id}
                className={`p-4 rounded-lg border-l-4 ${getSeverityColor(insight.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="material-symbols-outlined text-xl">
                      {getSeverityIcon(insight.severity)}
                    </span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {insight.title}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {insight.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => dismissInsight(insight.id)}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trend Chart (Simple Text Version) */}
      {trends.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            30-Tage-Trend
          </h3>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {trends.reduce((sum, t) => sum + t.cases_created, 0)}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">Fälle erstellt</div>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                {trends.reduce((sum, t) => sum + t.cases_closed, 0)}
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">Fälle geschlossen</div>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                {trends[trends.length - 1]?.cumulative_active || 0}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-300">Aktuell aktiv</div>
            </div>
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
            📊 Detaillierte Diagramme in Kürze verfügbar
          </div>
        </div>
      )}
    </div>
  );
};
